import React, { useEffect, useState, useRef } from "react";
import SubscriptionModal from "./SubscriptionModal";
import * as XLSX from "xlsx";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import Stack from "@mui/material/Stack";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import PropTypes from "prop-types";
import { useMaterialUIController } from "context";
import Payment from "./BulkPayment";
import CircularProgress from "@mui/material/CircularProgress";
import {
  Grid,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  TextField,
  FormLabel,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import TermsAndConditionsPDF from "../../assets/pdf/Terms&Conditions.pdf";
import PaymentTermsPDF from "../../assets/pdf/Payment_Terms.pdf";
import Refresh from "pages/refresh/Refresh";
import * as Sentry from "@sentry/browser";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Loader from "pages/loader/Loader";
import { call } from "services/axiosService/core";
import BulkPayment from "./BulkPayment";
import { useAuthenticator } from "@aws-amplify/ui-react";

const BulkUpload = ({
  adding,
  trueMindsUser,
  setOpenAddAthleteModal,
  setOpenAddChildModal,
  handleCloseBulkUploadModal,
  getChildrens,
  updateUserProfile,
  setOpenSnackbar,
  setAlertMessage,
  setSeverity,
  setSessionExpired,
  setProfile,
  t,
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [noPaymentRequired, setNoPaymentRequired] = useState(false);
  const [previewData, setPreviewData] = useState([]); // Excel preview
  const [step, setStep] = useState(0);
  const [noLearners, setNoLearners] = useState(false);
  const [learnerDetails, setLearnerDetails] = useState([]);
  const [existingLearners, setExistingLearners] = useState([]);
  const [amountToProcess, setAmountToProcess] = useState(0);
  const [averageCost, setAverageCost] = useState(0);
  const [subscriptionType, setSubscriptionType] = useState("maxu_yearly");
  const [termsConditionsChecked, setTermsConditionsChecked] = useState(false);
  const [paymentTermsChecked, setPaymentTermsChecked] = useState(false);
  const [manualLearners, setManualLearners] = useState([
    { first_name: "", last_name: "", email: "", gender: "", dob: "", code: "" },
  ]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [controller, dispatch] = useMaterialUIController();
  const { profile, persona } = controller;
  const { signOut } = useAuthenticator((context) => [context.user]);

  const handleSignupChild = async () => {
    await signOut();
  };

  useEffect(() => {
    let today = new Date();
    let end_date = new Date(
      today.getFullYear() - 8,
      today.getMonth(),
      today.getDate(),
      today.getHours(),
      today.getMinutes(),
      today.getSeconds()
    );
    setEndDate(end_date.toISOString().slice(0, 10));

    if (adding == "asParent") {
      let start_date = new Date(
        today.getFullYear() - 18,
        today.getMonth(),
        today.getDate() + 1,
        today.getHours(),
        today.getMinutes(),
        today.getSeconds()
      );
      setStartDate(start_date.toISOString().slice(0, 10));
    }
  }, []);

  const handleCloseSubscriptionModal = (sub) => {
    setSubscriptionType(sub);
    setStep(2);
  };

  const termsConditionsCheckboxHandler = () => {
    setTermsConditionsChecked(!termsConditionsChecked);
  };

  const paymentTermsCheckboxHandler = () => {
    setPaymentTermsChecked(!paymentTermsChecked);
  };

  const RequiredLabel = ({ children }) => {
    return (
      <FormLabel
        sx={{
          display: "flex",
          color: "black",
          alignItems: "center",
          margin: "10px auto 2px",
          fontSize: "1.05rem",
          fontWeight: 900,
        }}
      >
        <span style={{ fontSize: "small", marginRight: "5px" }}>{children}</span>
      </FormLabel>
    );
  };

  const formatDate = (value) => {
    const date = new Date(value);
    if (isNaN(date.getTime())) return ""; // Invalid date
    return date.toISOString().slice(0, 10); // YYYY-MM-DD
  };

  const handleManualChange = (index, field, value) => {
    const updated = [...manualLearners];
    updated[index][field] = value;
    setManualLearners(updated);
  };

  const addManualRow = () => {
    setManualLearners([
      ...manualLearners,
      { first_name: "", last_name: "", email: "", gender: "", dob: "", code: "" },
    ]);
  };

  const removeManualRow = (index) => {
    if (manualLearners.length > 1) {
      const updated = [...manualLearners];
      updated.splice(index, 1);
      setManualLearners(updated);
    }
  };

  const handleManualSubmit = async () => {
    const requiredFields = ["first_name", "last_name", "email", "gender", "dob"];

    const hasEmptyFields = manualLearners.some((learner) =>
      requiredFields.some((field) => !learner[field] || learner[field].trim() === "")
    );

    if (hasEmptyFields) {
      setAlertMessage(t("alerts.bulkUpload.allRequired"));
      setSeverity("error");
      setOpenSnackbar(true);
      return;
    }
    const hasInvalidEmail = manualLearners.some(
      (learner) => learner.email && !/\S+@\S+\.\S+/.test(learner.email)
    );

    const emailCounts = manualLearners.reduce((acc, learner) => {
      const email = learner.email.trim().toLowerCase();
      if (email) {
        acc[email] = (acc[email] || 0) + 1;
      }
      return acc;
    }, {});

    const hasDuplicateEmail = Object.values(emailCounts).some((count) => count > 1);
    if (hasInvalidEmail) {
      setAlertMessage(t("alerts.bulkUpload.enterEmail"));
      setSeverity("error");
      setOpenSnackbar(true);
      return;
    }
    if (hasDuplicateEmail) {
      setAlertMessage(t("alerts.bulkUpload.duplicateEmail"));
      setSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    if (!termsConditionsChecked || !paymentTermsChecked) {
      setAlertMessage(t("alerts.bulkUpload.acceptTerms"));
      setSeverity("error");
      setOpenSnackbar(true);
      return;
    }
    autoSubmit(manualLearners);
  };

  const handleUpload = (event) => {
    setUploading(true);
    setPreviewData([]);
    const file = event.target.files[0];
    if (!file) return;

    setTimeout(() => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          let invalidDate = false;
          let invalidDateRange = false;

          let rawJson = XLSX.utils.sheet_to_json(worksheet, {
            header: ["first_name", "last_name", "email", "gender", "dob", "code"],
            range: 1,
            raw: false,
            defval: "",
          });
          rawJson = rawJson.slice(0, 100);
          // Filter valid DOB & age
          const validLearners = rawJson
            .map((learner) => {
              const dob = learner.dob;
              const code = learner.code.trim();
              const [month, day, year] = learner.dob.split("/"); // from Excel
              if (!month || !day || !year) {
                invalidDate = true;
                return null;
              }
              const dobIso = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
              const birthDate = new Date(dobIso);

              if (isNaN(birthDate.getTime())) {
                invalidDate = true;
                return null;
              }

              const ageDiff = Date.now() - birthDate.getTime();
              const ageDate = new Date(ageDiff);
              const user_age = Math.abs(ageDate.getUTCFullYear() - 1970);

              if (
                (adding === "asCoach" && user_age < 8) ||
                (adding === "asParent" && (user_age < 8 || user_age > 18))
              ) {
                invalidDateRange = true;
                return null;
              }

              return {
                ...learner,
                dob: birthDate.toISOString().slice(0, 10),
                code: code,
                dob_display: learner.dob,
                user_age,
              };
            })
            .filter((item) => item !== null);

          if (invalidDate) {
            setAlertMessage(t("alerts.bulkUpload.invalidDOB"));
            setSeverity("error");
            setOpenSnackbar(true);
            return;
          }
          if (invalidDateRange) {
            setAlertMessage(t("alerts.bulkUpload.invalidAge"));
            setSeverity("error");
            setOpenSnackbar(true);
            return;
          }
          const emailRegex = /^\S+@\S+\.\S+$/;
          const allEmailsValid = validLearners.every(
            (learner) => learner.email && emailRegex.test(learner.email)
          );

          if (!allEmailsValid) {
            setAlertMessage(t("alerts.bulkUpload.invalidEmail"));
            setSeverity("error");
            setOpenSnackbar(true);
            return;
          }
          const emailMap = {};
          let hasDuplicate = false;
          for (const learner of validLearners) {
            const email = learner.email.trim().toLowerCase();
            if (emailMap[email]) {
              hasDuplicate = true;
              break;
            }
            emailMap[email] = true;
          }

          if (hasDuplicate) {
            setAlertMessage(t("alerts.bulkUpload.duplicateEmailInFile"));
            setSeverity("error");
            setOpenSnackbar(true);
            return;
          }

          console.log("validLearners", validLearners);
          setPreviewData(validLearners);
        } catch (err) {
          console.error("Error processing file:", err);
          setAlertMessage(t("alerts.bulkUpload.errorFile"));
          setSeverity("error");
          setOpenSnackbar(true);
        } finally {
          setUploading(false);
        }
      };

      reader.readAsArrayBuffer(file);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }, 0);
  };

  const handleSubmit = async () => {
    autoSubmit(previewData);
  };

  const autoSubmit = async (data) => {
    try {
      const learners = [];
      data.forEach((learner) => {
        const name =
          learner.first_name.replace(/\s+/g, "_") + " " + learner.last_name.replace(/\s+/g, "_");
        const dob = learner.dob;
        const birthDate = new Date(dob);
        if (isNaN(birthDate.getTime())) return null;
        const ageDiff = Date.now() - birthDate.getTime();
        const ageDate = new Date(ageDiff);
        const user_age = Math.abs(ageDate.getUTCFullYear() - 1970);
        let relationships = { parents: [profile.id] };
        const code = learner.code;
        const age = learner.user_age ? learner.user_age : user_age;
        if (adding === "asCoach") {
          relationships =
            age < 18 ? { coaches: [profile.id] } : { coaches: [profile.id], children: [] };
        }
        const learner_data = {
          email: learner.email,
          phone: null,
          relationships: relationships,
          cognito_user_id: null,
          type: age < 18 ? ["Learner"] : ["Learner", "Adult"],
          language: profile.language || "en",
          is_demo_user: false,
          status: "inactive",
          true_mindsets: !!profile.true_mindsets,
          source: profile.source
            ? profile.source
            : code && code.includes("_")
            ? code.split("_")[0] + "_code"
            : null,
          details: {
            name: name,
            dob: learner.dob,
            persona: ["Athlete"],
            gender: learner.gender,
            address: {
              street: null,
              city: null,
              state: null,
              country: null,
              zip: null,
            },
          },
          payment_status: "pending",
          profile_image_url: null,
          referral_code: code ? code : null,
          identities: [
            {
              google: { id: "" },
              facebook: { id: "" },
              apple: { id: "" },
              amazon: { id: "" },
              email: { id: "" },
            },
          ],
        };
        learners.push(learner_data);
      });

      setPreviewData([]);
      setStep(10);

      const body = JSON.stringify({
        learners: learners,
        added_as: adding,
        guardian_id: profile.id,
        subscription_type: subscriptionType,
      });
      const response = await call("users/bulk-upload", "POST", {
        body: body,
        headers: { "Content-Type": "application/json" },
      });
      if (response.status == 200) {
        const hasSummary = Array.isArray(response.data.summary) && response.data.summary.length > 0;
        setNoLearners(!hasSummary);
        setAlertMessage(hasSummary ? t("alerts.bulkUpload.success") : t("alerts.bulkUpload.error"));
        setSeverity(hasSummary ? "success" : "error");
        setOpenSnackbar(true);
        setLearnerDetails(response.data.summary);
        setExistingLearners(response.data.existing_learners);
        getChildrens();
        updateUserProfile();
        if (response.data.total_amount > 0) {
          setAverageCost((response.data.total_amount / response.data.summary.length).toFixed(2));
          setAmountToProcess(response.data.total_amount);
        } else {
          setAverageCost(0);
          setAmountToProcess(0);
          setNoPaymentRequired(true);
        }
        setStep(5);
        // setSnackbarMessage("Learners added successfully!");
        // setSnackbarSeverity("success");
        setPreviewData([]);
      } else if (response.status == 440) {
        setPreviewData([]);
        setSessionExpired(true);
      } else {
        setPreviewData([]);
        setAlertMessage(t("alerts.bulkUpload.error"));
        setSeverity("error");
        setOpenSnackbar(true);
      }
    } catch (err) {
      setUploading(false);
      setAlertMessage(t("alerts.bulkUpload.error"));
      setSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setUploading(false);
    }
  };

  const proceedToPayment = async () => {
    if (noLearners) {
      handleCloseBulkUploadModal();
      return;
    }
    if (noPaymentRequired) {
      setStep(10);
      // if we want to collect the payment info for the future , we can do it here
      // enable the paymentSetupWrapper after upating the all the learner details
      // after add the payment info, show the instructions to add children
      const usages = learnerDetails
        .filter((learner) => learner.code_status === "valid_code" && learner.referral_code)
        .map((learner) => ({
          referral_code: learner.referral_code,
          email: learner.email,
        }));
      const learner_ids = learnerDetails.map((learner) => learner.learner_id);
      const AdultDetails = profile;
      let true_users = profile.true_mindsets_users ? profile.true_mindsets_users : [];
      let true_subs = ["true_mindsets_yearly", "true_mindsets_monthly"];
      if (true_subs.includes(subscriptionType)) {
        true_users = Array.from(new Set([...true_users, ...learner_ids]));
        AdultDetails.true_mindsets_users = true_users;
        call(`users/${profile.id}`, "PUT", {
          body: AdultDetails,
          headers: { "Content-Type": "application/json" },
        })
          .then((response) => {
            if (response.status == 200) {
              console.log("update Success");
              updateUserProfile();
            } else if (response.status == 440) {
              setSessionExpired(true);
            }
          })
          .catch(function (error) {
            Sentry.captureException(error);
            console.log(error);
          });
      }

      if (profile.is_demo_user == false) {
        profile.is_demo_user = true;
        call(`users/${profile.id}`, "PUT", {
          body: profile,
          headers: { "Content-Type": "application/json" },
        })
          .then((response) => {
            if (response.status == 200) {
              setProfile(profile);
            } else if (response.status == 440) {
              setSessionExpired(true);
            }
          })
          .catch(function (error) {
            Sentry.captureException(error);
            console.log(error);
          });
      }

      if (usages.length > 0) {
        call(`/payments/update-bulk-coupon-usage`, "PUT", {
          body: {
            usages: usages,
          },
        })
          .then((response) => {
            if (response.status == 200) {
              console.log("update Success");
            } else if (response.status == 440) {
              setSessionExpired(true);
            }
          })
          .catch(function (error) {
            Sentry.captureException(error);
            console.log(error);
          });
      }

      call(`/users/bulk-upload/update-status`, "POST", {
        body: {
          learner_details: learnerDetails,
          payment_status: "success",
          paid_at: new Date(),
          subscription_type: subscriptionType,
        },
      })
        .then((response) => {
          if (response.status == 200) {
            console.log("update Success");
            getChildrens();
            setStep(9);
          } else if (response.status == 440) {
            setSessionExpired(true);
          }
        })
        .catch(function (error) {
          Sentry.captureException(error);
          console.log(error);
        });
    } else {
      setStep(6);
    }
  };

  return (
    <Dialog
      open={true}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "80vw", // Set desired width (e.g., 80% of viewport)
          maxWidth: "1000px", // Optional: max limit
          borderRadius: "16px",
        },
      }}
    >
      <CloseIcon
        fontSize="medium"
        onClick={handleCloseBulkUploadModal}
        style={{ color: "red", display: "block", marginLeft: "auto", cursor: "pointer" }}
      />

      {step == 0 && (
        <>
          <DialogTitle tabindex="0">
            <Typography variant="h4" textAlign="center" mb={2}>
              {t("bulkUpload.headings.addChildren")}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} alignItems="center" mt={1}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#10B0D5",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#089ebe",
                    color: "#fff",
                  },
                  textTransform: "none",
                  fontSize: "14px",
                  borderRadius: "12px",
                  px: 3,
                  py: 1.5,
                }}
                fullWidth
                onClick={() => {
                  if (adding == "asParent") {
                    setOpenAddChildModal(true);
                    handleCloseBulkUploadModal();
                  } else {
                    setOpenAddAthleteModal(true);
                    handleCloseBulkUploadModal();
                  }
                }}
              >
                {t("buttonTitles.addOneChild")}
              </Button>

              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#10B0D5",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#089ebe",
                    color: "#fff",
                  },
                  textTransform: "none",
                  fontSize: "14px",
                  borderRadius: "12px",
                  px: 3,
                  py: 1.5,
                }}
                fullWidth
                onClick={() => {
                  trueMindsUser ? setStep(1) : setStep(2);
                }}
              >
                {t("buttonTitles.addMultipleChildren")}
              </Button>
            </Stack>
          </DialogContent>
        </>
      )}

      {step == 1 && (
        <DialogContent>
          <SubscriptionModal isOpen={true} onClose={handleCloseSubscriptionModal} />
        </DialogContent>
      )}

      {step == 2 && (
        <>
          <DialogTitle tabindex="0">
            <Typography variant="h4" textAlign="center" mb={2}>
              {t("bulkUpload.headings.addChildren")}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} alignItems="center" mt={1}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#f6821e",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#eb6f05" },
                  textTransform: "none",
                  fontSize: "14px",
                  borderRadius: "12px",
                  px: 3,
                  py: 1.5,
                }}
                fullWidth
                onClick={() => {
                  setStep(4);
                }}
              >
                {t("buttonTitles.addChildrenManually")}
              </Button>

              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#f6821e",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#eb6f05" },
                  textTransform: "none",
                  fontSize: "14px",
                  borderRadius: "12px",
                  px: 3,
                  py: 1.5,
                }}
                fullWidth
                onClick={() => {
                  setStep(3);
                }}
              >
                {t("buttonTitles.bulkUpload")}
              </Button>
            </Stack>
          </DialogContent>
        </>
      )}

      {step == 3 && (
        <>
          <DialogTitle tabindex="0">
            <Typography variant="h4" textAlign="center" mb={2}>
              {t("headings.addLearner")}
            </Typography>
            <Typography variant="h6" textAlign="center" mb={2}>
              {adding == "asParent"
                ? t("messages.profile.addAsParent")
                : t("messages.profile.addAsCoach")}
            </Typography>
            <Typography variant="h6" textAlign="center" color="#1976d2" mb={2}>
              {t("bulkUpload.labels.instructions")}:
              <br /> {t("bulkUpload.instructions.instruction1")}
              <br /> {t("bulkUpload.instructions.instruction2")}
              <br /> {t("bulkUpload.instructions.instruction3")}
              <br /> {t("bulkUpload.instructions.instruction4")}
              <br /> {t("bulkUpload.instructions.instruction5")}
              <br /> {t("bulkUpload.instructions.instruction6")}
              <br /> {t("bulkUpload.instructions.instruction7")}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} alignItems="center" mt={1}>
              {/* Download Template */}
              <Button
                variant="outlined"
                component="a"
                href="/assets/learners_data.xlsx"
                download
                sx={{
                  backgroundColor: "#10B0D5",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#089ebe",
                    color: "#fff",
                  },
                  textTransform: "none",
                  fontSize: "14px",
                  borderRadius: "12px",
                  px: 3,
                  py: 1.5,
                  width: "100%",
                }}
              >
                <DownloadForOfflineIcon sx={{ fontSize: 24, mr: 2 }} />
                {t("buttonTitles.downloadTemplate")}
              </Button>

              {/* Upload Field */}
              <Button
                variant="contained"
                component="label"
                sx={{
                  backgroundColor: "#10B0D5",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#089ebe",
                    color: "#fff",
                  },
                  textTransform: "none",
                  fontSize: "14px",
                  borderRadius: "12px",
                  px: 3,
                  py: 1.5,
                  width: "100%",
                }}
              >
                <UploadFileIcon sx={{ fontSize: 24, mr: 2 }} />
                {uploading ? t("buttonTitles.uploading") : t("buttonTitles.uploadFile")}
                <input
                  type="file"
                  disabled={uploading}
                  hidden
                  ref={fileInputRef}
                  accept=".xlsx"
                  onChange={handleUpload}
                />
              </Button>

              {previewData.length > 0 && (
                <Stack spacing={2} sx={{ width: "100%", mt: 1 }}>
                  <Typography variant="h6" textAlign="center">
                    {t("bulkUpload.headings.preview")}
                  </Typography>
                  <div style={{ maxHeight: 300, overflow: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          {[
                            "first_name",
                            "last_name",
                            "email",
                            "gender",
                            "dob_display",
                            "code",
                            "user_age",
                          ].map((key) => (
                            <th
                              key={key}
                              style={{
                                border: "1px solid #ccc",
                                padding: "8px",
                                background: "#f0f0f0",
                              }}
                            >
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.map((row, index) => (
                          <tr key={index}>
                            {[
                              "first_name",
                              "last_name",
                              "email",
                              "gender",
                              "dob_display",
                              "code",
                              "user_age",
                            ].map((key) => (
                              <td
                                key={key}
                                style={{
                                  border: "1px solid #ddd",
                                  padding: "6px",
                                  textAlign: "center",
                                }}
                              >
                                {row[key]}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <form onSubmit={handleSubmit}>
                    <FormGroup sx={{ marginTop: "1px" }}>
                      <MDBox display="flex" direction="row" gap={1}>
                        <MDTypography variant="h6" style={{ paddingTop: "10px" }}>
                          <Checkbox
                            required
                            inputProps={{ "aria-label": t("formLabels.termsAndConditions") }}
                            checked={termsConditionsChecked}
                            onChange={termsConditionsCheckboxHandler}
                          />
                        </MDTypography>
                        <MDTypography variant="h6" style={{ paddingTop: "18px" }}>
                          <span style={{ color: "red", fontSize: "small", paddingRight: "5px" }}>
                            *
                          </span>
                          {t("formLabels.termsAndConditions")}
                          <Typography
                            component="a"
                            fontSize={15}
                            color="#10b0d5"
                            href={"/terms_conditions"}
                            target="_blank"
                            style={{ paddingTop: "15px" }}
                          >
                            {" "}
                            {t("formLabels.terms")}
                          </Typography>
                          <span style={{ color: "red", fontSize: "small", marginLeft: "10px" }}>
                            {t("formLabels.required")}
                          </span>
                        </MDTypography>
                      </MDBox>
                      <MDBox display="flex" direction="row" gap={1}>
                        <MDTypography style={{ paddingTop: "10px" }}>
                          <Checkbox
                            required
                            inputProps={{ "aria-label": t("formLabels.paymentTerms") }}
                            checked={paymentTermsChecked}
                            onChange={paymentTermsCheckboxHandler}
                          />
                        </MDTypography>
                        <MDTypography variant="h6" style={{ paddingTop: "18px" }}>
                          <span style={{ color: "red", fontSize: "small", paddingRight: "5px" }}>
                            *
                          </span>
                          {t("formLabels.termsAndConditions")}
                          <Typography
                            component="a"
                            fontSize={15}
                            color="#10b0d5"
                            href={"/payment_terms"}
                            target="_blank"
                            style={{ paddingTop: "15px" }}
                          >
                            {" "}
                            {t("formLabels.paymentTerms")}
                          </Typography>
                          <span style={{ color: "red", fontSize: "small", marginLeft: "10px" }}>
                            {t("formLabels.required")}
                          </span>
                        </MDTypography>
                      </MDBox>
                    </FormGroup>

                    <Button
                      variant="contained"
                      type="submit"
                      sx={{
                        backgroundColor: "#f6821e",
                        color: "#fff",
                        "&:hover": { backgroundColor: "#eb6f05" },
                        width: "100%",
                        textTransform: "none",
                        fontSize: "14px",
                        borderRadius: "12px",
                        px: 3,
                        py: 1.5,
                      }}
                    >
                      {t("buttonTitles.submitLearners")}
                    </Button>
                  </form>
                </Stack>
              )}
            </Stack>
          </DialogContent>
        </>
      )}

      {step === 4 && (
        <>
          <DialogTitle tabindex="0">
            <Typography variant="h4" textAlign="center" mb={2}>
              {t("bulkUpload.headings.addChildrenDetails")}
            </Typography>
            <Typography variant="h6" textAlign="center" mb={2}>
              {adding == "asParent"
                ? t("messages.profile.addAsParent")
                : t("messages.profile.addAsCoach")}
            </Typography>
            <Typography variant="h6" textAlign="center" color="#1976d2" mb={2}>
              {t("bulkUpload.labels.instructions")}:
              <br /> {t("bulkUpload.instructions.instruction3")}
              <br /> {t("bulkUpload.instructions.instruction5")}
              <br /> {t("bulkUpload.instructions.instruction7")}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1}>
              <Grid container spacing={2} alignItems="center">
                {manualLearners.map((learner, index) => (
                  <Grid
                    container
                    key={index}
                    spacing={1}
                    border={1}
                    m={2}
                    pb={1}
                    pr={1}
                    boxShadow={"3"}
                    borderColor={"#0c85ed"}
                  >
                    <Grid item xs={2}>
                      <RequiredLabel> * {t("formLabels.firstName")}</RequiredLabel>
                      <TextField
                        fullWidth
                        value={learner.first_name}
                        onChange={(e) => handleManualChange(index, "first_name", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <RequiredLabel>* {t("formLabels.lastName")}:</RequiredLabel>
                      <TextField
                        fullWidth
                        value={learner.last_name}
                        onChange={(e) => handleManualChange(index, "last_name", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <RequiredLabel>* {t("formLabels.email")}:</RequiredLabel>
                      <TextField
                        fullWidth
                        value={learner.email}
                        onChange={(e) => handleManualChange(index, "email", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={1.5}>
                      <RequiredLabel>* {t("formLabels.gender")}:</RequiredLabel>
                      <FormControl fullWidth>
                        <Select
                          style={{ height: "45px" }}
                          value={learner.gender}
                          onChange={(e) => handleManualChange(index, "gender", e.target.value)}
                          aria-label={"Select Gender"}
                          name="gender"
                        >
                          <MenuItem aria-label="Male" value="Male">
                            {t("formLabels.male")}
                          </MenuItem>
                          <MenuItem aria-label="Female" value="Female">
                            {t("formLabels.female")}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={2}>
                      <RequiredLabel>* {t("formLabels.birthdate")}</RequiredLabel>
                      <TextField
                        type="date"
                        id="dob"
                        aria-describedby="my-helper-text"
                        name="dob"
                        onChange={(e) => handleManualChange(index, "dob", e.target.value)}
                        value={learner.dob}
                        inputProps={{
                          "aria-label": "Enter Child/Athlete's Date of Birth",
                          min: startDate,
                          max: endDate,
                        }}
                        required
                        fullWidth
                        style={{ display: "block" }}
                      />
                    </Grid>
                    <Grid item xs={1.5}>
                      <RequiredLabel> {t("formLabels.code")} </RequiredLabel>
                      <TextField
                        fullWidth
                        value={learner.code}
                        onChange={(e) => handleManualChange(index, "code", e.target.value.trim())}
                      />
                    </Grid>
                    <Grid item xs={12} container justifyContent="flex-end" alignItems="flex-end">
                      {index > 0 && (
                        <Button
                          onClick={() => removeManualRow(index)}
                          size="small"
                          sx={{
                            backgroundColor: "#f44336",
                            color: "#fff",
                            minWidth: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            fontWeight: "bold",
                            "&:hover": {
                              backgroundColor: "#d32f2f",
                            },
                          }}
                          aria-label="Remove learner"
                        >
                          {t("buttonTitles.remove")}
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Grid>

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                {manualLearners.length < 100 && (
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#10B0D5",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#089ebe",
                        color: "#fff",
                      },
                      textTransform: "none",
                      fontSize: "14px",
                      borderRadius: "12px",
                      px: 3,
                      py: 1.5,
                    }}
                    onClick={addManualRow}
                  >
                    {t("buttonTitles.addAthlete")}
                  </Button>
                )}
              </Stack>
              <FormGroup>
                <MDBox display="flex" direction="row" gap={1}>
                  <MDTypography variant="h6" style={{ paddingTop: "10px" }}>
                    <Checkbox
                      required
                      inputProps={{ "aria-label": t("formLabels.termsAndConditions") }}
                      checked={termsConditionsChecked}
                      onChange={termsConditionsCheckboxHandler}
                    />
                  </MDTypography>
                  <MDTypography variant="h6" style={{ paddingTop: "18px" }}>
                    <span style={{ color: "red", fontSize: "small", paddingRight: "5px" }}>*</span>
                    {t("formLabels.termsAndConditions")}
                    <Typography
                      component="a"
                      fontSize={15}
                      color="#10b0d5"
                      href={"/terms_conditions"}
                      target="_blank"
                      style={{ paddingTop: "15px" }}
                    >
                      {" "}
                      {t("formLabels.terms")}
                    </Typography>
                    <span style={{ color: "red", fontSize: "small", marginLeft: "10px" }}>
                      {t("formLabels.required")}
                    </span>
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" direction="row" gap={1}>
                  <MDTypography style={{ paddingTop: "10px" }}>
                    <Checkbox
                      required
                      inputProps={{ "aria-label": t("formLabels.paymentTerms") }}
                      checked={paymentTermsChecked}
                      onChange={paymentTermsCheckboxHandler}
                    />
                  </MDTypography>
                  <MDTypography variant="h6" style={{ paddingTop: "18px" }}>
                    <span style={{ color: "red", fontSize: "small", paddingRight: "5px" }}>*</span>
                    {t("formLabels.termsAndConditions")}
                    <Typography
                      component="a"
                      fontSize={15}
                      color="#10b0d5"
                      href={"/payment_terms"}
                      target="_blank"
                      style={{ paddingTop: "15px" }}
                    >
                      {" "}
                      {t("formLabels.paymentTerms")}
                    </Typography>
                    <span style={{ color: "red", fontSize: "small", marginLeft: "10px" }}>
                      {t("formLabels.required")}
                    </span>
                  </MDTypography>
                </MDBox>
              </FormGroup>
              <Button
                variant="contained"
                onClick={handleManualSubmit}
                sx={{
                  backgroundColor: "#f6821e",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#eb6f05" },
                  width: "100%",
                  textTransform: "none",
                  fontSize: "14px",
                  borderRadius: "12px",
                  px: 3,
                  py: 1.5,
                }}
              >
                {t("buttonTitles.submitAthletes")}
              </Button>
            </Stack>
          </DialogContent>
        </>
      )}

      {step === 5 && (
        <>
          <DialogTitle>
            <Typography variant="h4" textAlign="center" mb={2}>
              {t("bulkUpload.headings.paymentSummary")}
            </Typography>
          </DialogTitle>

          <DialogContent>
            <MDBox
              mb={2}
              p={2}
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{
                backgroundColor: "#f9f9f9",
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              <Typography variant="h5" mb={1}>
                {t("bulkUpload.headings.averageCost")}
              </Typography>
              <Typography variant="h5" ml={1} mb={1} color="primary">
                ${averageCost}
              </Typography>
            </MDBox>

            <Stack spacing={2} maxHeight={400} sx={{ overflowY: "auto", pr: 1 }}>
              {learnerDetails.map((item, index) => (
                <MDBox
                  key={index}
                  p={2}
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "12px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                    borderLeft: `5px solid ${
                      item.code_status === "valid_code"
                        ? "#4caf50"
                        : item.code_status === "limit_exceeded"
                        ? "#ff9800"
                        : "#7fe5f5"
                    }`,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <MDBox>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {item.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("formLabels.email")}: {item.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("formLabels.code")}: {item.referral_code || "—"}
                    </Typography>

                    <MDBox
                      mt={1}
                      display="inline-block"
                      px={2}
                      py={0.5}
                      borderRadius="8px"
                      sx={{
                        backgroundColor:
                          item.code_status === "valid_code"
                            ? "#e8f5e9"
                            : item.code_status === "limit_exceeded"
                            ? "#fff3e0"
                            : "#ffebee",
                        color:
                          item.code_status === "valid_code"
                            ? "#388e3c"
                            : item.code_status === "limit_exceeded"
                            ? "#ef6c00"
                            : "#c62828",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        width: "fit-content",
                      }}
                    >
                      {item.code_status.replaceAll("_", " ").toUpperCase()}
                    </MDBox>
                  </MDBox>

                  {/* Price Section on the right */}
                  <MDBox
                    sx={{
                      minWidth: "80px",
                      textAlign: "right",
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      ${item.price}
                    </Typography>
                  </MDBox>
                </MDBox>
              ))}

              {existingLearners.map((item, index) => (
                <MDBox
                  key={index}
                  p={2}
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "12px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                    borderLeft: `5px solid #f44336`,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <MDBox>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {item.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("formLabels.email")}: {item.email}
                    </Typography>

                    <MDBox
                      mt={1}
                      display="inline-block"
                      px={2}
                      py={0.5}
                      borderRadius="8px"
                      sx={{
                        backgroundColor: "#ffebee",
                        color: "#c62828",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        width: "fit-content",
                      }}
                    >
                      {t("bulkUpload.labels.userExisted")}
                    </MDBox>
                  </MDBox>
                </MDBox>
              ))}
            </Stack>

            <MDBox mt={3} mb={1}>
              <Typography variant="h6" textAlign="center">
                {t("bulkUpload.labels.totalAmount")} ${amountToProcess}
              </Typography>
            </MDBox>

            <Button
              variant="contained"
              fullWidth
              sx={{
                mt: 2,
                backgroundColor: "#f6821e",
                color: "#fff",
                "&:hover": { backgroundColor: "#eb6f05" },
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 600,
                borderRadius: "12px",
                px: 3,
                py: 1.5,
              }}
              onClick={() => {
                proceedToPayment();
                // Proceed to payment logic here
              }}
            >
              {noLearners
                ? t("buttonTitles.close")
                : noPaymentRequired
                ? t("buttonTitles.proceedToFinish")
                : t("buttonTitles.payNow")}
            </Button>
          </DialogContent>
        </>
      )}

      {step == 6 && (
        <>
          <DialogTitle>
            <Typography variant="h4" textAlign="center" mb={1}>
              {t("headings.maxuPayment")}
            </Typography>
            <Typography variant="h6" textAlign="center" mb={2}>
              {t(`messages.subscription.maxuAnnualFeeBulk`, { amount: averageCost })}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <BulkPayment
              learnerDetails={learnerDetails}
              amountToProcess={amountToProcess}
              averageCost={averageCost}
              subscriptionType={subscriptionType}
              setStep={setStep}
              setSessionExpired={setSessionExpired}
              getChildrens={getChildrens}
              updateUserProfile={updateUserProfile}
              setOpenSnackbar={setOpenSnackbar}
              setAlertMessage={setAlertMessage}
              setSeverity={setSeverity}
            />
          </DialogContent>
        </>
      )}

      {/* {step == 7 && (
        <>
          <DialogTitle tabindex="0">
            <Typography variant="h4" textAlign="center" mb={2}>
              {t("headings.addLearner")}
            </Typography>
            <Typography variant="h6" textAlign="center" mb={2}>
              No Payment required,Proceed to finish.
            </Typography>
          </DialogTitle>
        </>
      )} */}

      {step == 8 && (
        <>
          <DialogContent>
            <Typography variant="h4" ma={1}>
              {t("messages.subscription.paymentCompleted")}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setStep(9)}
              style={{
                marginTop: "15px",
                color: "white",
                background: "#10B0D5",
              }}
            >
              {t("buttonTitles.finish")}
            </Button>
          </DialogActions>
        </>
      )}

      {step == 9 && (
        <DialogContent>
          <Typography
            variant="body1"
            fontSize={16}
            textAlign="left"
            sx={{ whiteSpace: "normal", lineHeight: 1.7 }}
            dangerouslySetInnerHTML={{ __html: t("messages.subscription.thankyouMessageChild") }}
          />

          <MDBox display="flex" justifyContent="space-between" mt={2}>
            <Button
              type="button"
              onClick={handleSignupChild}
              style={{
                color: "white",
                background: "#f6821e",
              }}
            >
              {t("buttonTitles.signupChild")}
            </Button>
            <Button
              type="button"
              onClick={handleCloseBulkUploadModal}
              style={{
                color: "white",
                background: "#10B0D5",
                marginTop: "15px",
                display: "flex",
                justifyContent: "end",
              }}
            >
              {t("buttonTitles.close")}
            </Button>
          </MDBox>
        </DialogContent>
      )}
      {step == 10 && (
        <DialogContent
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "300px",
            overflow: "hidden",
            padding: 4,
          }}
        >
          <Loader loaderText={t("loaderText.bulkAdd")} />
        </DialogContent>
      )}
    </Dialog>
  );
};

export default BulkUpload;
